import React, { Fragment, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChromePicker } from 'react-color';

import withModal from '../../../../../../../../../../components/global/modal/withModal';
import Button from '../../../../../../../../../../components/global/Button';
import useClickOutside from '../../../../../../../../../../hooks/useClickOutside';
import DismissButton from '../../../../../../../../../../components/global/DismissButton';
import Label from '../../../../../../../../../../components/global/Label';
import useOpenAlert from '../../../../../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../../../../../util/normalize-errors';

const INTRO_SLIDE_MESSAGE_CONFIGURATOR =
  'modal/INTRO_SLIDE_MESSAGE_CONFIGURATOR';

const propTypes = {
  text: PropTypes.string,
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  onIntroSlideMessageUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const IntroSlideMessageConfigurator = ({
  text: currentText,
  color: currentColor,
  backgroundColor: currentBackgroundColor,
  onIntroSlideMessageUpdate,
  onClose,
}) => {
  const openAlert = useOpenAlert();
  const [saving, setSaving] = useState(false);
  const [text, setText] = useState((currentText ?? '').replace(/\n\n+/g, '\n'));
  const textRef = useRef(null);
  const colorRef = useRef(null);
  const bgColorRef = useRef(null);
  const [color, setColor] = useState(currentColor ?? null);
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState(
    currentBackgroundColor ?? null,
  );
  const [displayBackgroundColorPicker, setDisplayBackgroundColorPicker] =
    useState(false);

  useEffect(() => {
    if (textRef?.current) {
      textRef.current.focus();
    }
  }, [textRef]);

  useClickOutside(colorRef, () => {
    setDisplayColorPicker(false);
  });

  useClickOutside(bgColorRef, () => {
    setDisplayBackgroundColorPicker(false);
  });

  const onConfirm = async () => {
    if (saving) {
      return;
    }

    setSaving(true);
    try {
      await onIntroSlideMessageUpdate({
        text: text.replace(/\n+/g, '\n\n'),
        color,
        backgroundColor,
      });
      onClose();
    } catch (e) {
      openAlert(getErrorString(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <div className="text-right mb-1">
        <DismissButton
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
        />
      </div>
      <h4 className="text-center mb-0">Edit cover message</h4>
      <p className="text-bold text-light text-sm text-center">
        Press on text to edit
      </p>
      <div
        className="intro-slide-label inline mb-2"
        style={{ backgroundColor }}
      >
        <p className="text-uppercase text-center mb-0">
          <textarea
            ref={textRef}
            value={text}
            rows={3}
            cols={20}
            className="text-center text-sm text-black text-uppercase"
            style={{ color }}
            onChange={({ target }) => setText(target.value)}
          />
        </p>
      </div>

      <Label id="text-color">Text color</Label>
      {}
      <div
        ref={colorRef}
        id="text-color"
        role="button"
        tabIndex={0}
        className="intro-slide-label-color-button mb-1"
        onClick={() => setDisplayColorPicker(true)}
      >
        <div
          className="intro-slide-label-color-control-addon"
          style={{ backgroundColor: color }}
        />
        <span className="intro-slide-label-color-control-text">
          Change text color
        </span>
        {displayColorPicker ? (
          <div className="intro-slide-label-color-control-popover">
            <ChromePicker
              color={color}
              onChange={value => setColor(value.hex)}
              onChangeComplete={value => {
                setColor(value.hex);
              }}
              width="100%"
              disableAlpha
            />
          </div>
        ) : null}
      </div>

      <Label id="background-color">Background color</Label>
      {}
      <div
        ref={bgColorRef}
        id="background-color"
        role="button"
        tabIndex={0}
        className="intro-slide-label-color-button mb-2"
        onClick={() => setDisplayBackgroundColorPicker(true)}
      >
        <div
          className="intro-slide-label-color-control-addon"
          style={{ backgroundColor }}
        />
        <span className="intro-slide-label-color-control-text">
          Change background color
        </span>
        {displayBackgroundColorPicker ? (
          <div className="intro-slide-label-color-control-popover">
            <ChromePicker
              color={backgroundColor}
              onChange={value => setBackgroundColor(value.hex)}
              onChangeComplete={value => {
                setBackgroundColor(value.hex);
              }}
              width="100%"
              disableAlpha
            />
          </div>
        ) : null}
      </div>

      <div className="text-center">
        <Button
          disabled={saving}
          onClick={e => {
            e.stopPropagation();
            onConfirm();
          }}
        >
          {saving ? 'Saving...' : 'Confirm'}
        </Button>
      </div>
    </Fragment>
  );
};

IntroSlideMessageConfigurator.propTypes = propTypes;

export default withModal(IntroSlideMessageConfigurator, {
  name: INTRO_SLIDE_MESSAGE_CONFIGURATOR,
  size: 'sm',
  hideHeader: true,
});
